import React, { useCallback, useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";

/**
 * BackendPortal.
 *
 * Discreet entry point into the existing admin dashboard. Renders a
 * tiny gold star (12px) inline with the footer copyright row. A
 * casual visitor reads it as decoration. The owner clicks it,
 * enters the admin password, and lands directly on /admin.
 *
 * Implementation notes:
 *   - Reuses the existing /api/admin/login endpoint (single password
 *     gate, brute-force protected upstream).
 *   - Stores the resulting JWT under the same localStorage key the
 *     AdminPage already reads ("am-admin-token") so the dashboard
 *     boots straight into the authenticated view, no second prompt.
 *   - If a token is already present, clicking the star skips the
 *     prompt and navigates directly to /admin.
 *   - Esc and backdrop click close the modal. Focus moves to the
 *     password field on open.
 *   - No new backend, no new schema, no env vars. Pure UI shim on
 *     top of the live admin auth flow.
 */
const TOKEN_KEY = "am-admin-token";

const BackendPortal = () => {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);

  const openPortal = useCallback(() => {
    // Fast path: already signed in, skip the prompt.
    try {
      if (localStorage.getItem(TOKEN_KEY)) {
        window.location.href = "/admin";
        return;
      }
    } catch {
      /* localStorage unavailable; fall through to prompt */
    }
    setOpen(true);
  }, []);

  const closePortal = useCallback(() => {
    setOpen(false);
    setPw("");
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closePortal();
    };
    window.addEventListener("keydown", onKey);
    setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closePortal]);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/login", { password: pw });
      try {
        localStorage.setItem(TOKEN_KEY, r.data.token);
      } catch {
        /* token still works for this session even if storage blocked */
      }
      toast.success("Signed in.");
      window.location.href = "/admin";
    } catch (err) {
      const detail = err?.response?.data?.detail || "Access denied.";
      toast.error(detail);
      setPw("");
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openPortal}
        aria-label="Site administration"
        title=""
        data-testid="backend-portal-star"
        className="inline-flex items-center justify-center w-4 h-4 ml-2 align-middle text-gold/30 hover:text-gold/85 transition-colors focus:outline-none focus:text-gold rounded-full"
      >
        <Star size={11} strokeWidth={1.4} fill="currentColor" aria-hidden />
      </button>

      {open ? (
        <div
          data-testid="backend-portal-overlay"
          className="fixed inset-0 z-[120] flex items-center justify-center px-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePortal();
          }}
        >
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            aria-hidden
          />
          <form
            onSubmit={submit}
            data-testid="backend-portal-form"
            className="relative w-full max-w-sm bg-[#0a0a0a]/95 border border-gold/20 rounded-lg p-8 shadow-[0_24px_60px_rgba(0,0,0,0.6)]"
          >
            <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-3">
              Site Administration
            </p>
            <h2 className="font-serif text-2xl text-themed leading-tight font-light">
              Enter to continue.
            </h2>
            <input
              ref={inputRef}
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="Admin password"
              data-testid="backend-portal-password"
              autoComplete="current-password"
              className="mt-6 w-full bg-transparent border-b border-gold/25 focus:border-gold/70 text-themed text-base py-2 outline-none transition-colors"
            />
            <div className="mt-8 flex items-center gap-3">
              <button
                type="submit"
                disabled={busy || !pw}
                data-testid="backend-portal-submit"
                className="btn-gold flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? "Signing in..." : "Enter"}
              </button>
              <button
                type="button"
                onClick={closePortal}
                data-testid="backend-portal-cancel"
                className="text-[12px] tracking-[0.22em] uppercase text-themed-faint hover:text-gold transition-colors px-2"
              >
                Close
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </>
  );
};

export default BackendPortal;
